import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
    LayoutDashboard,
    GraduationCap,
    Zap,
    Trophy,
    User,
    Settings,
    LogOut,
    Flame,
    Users as UsersIcon,
    ChevronDown,
    Sparkles,
    Heart,
    HeartHandshake,
    UserPlus,
    Wallet,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";
import NotificationsBell from "./NotificationsBell";
import PointsPill from "./PointsPill";
import api from "../lib/api";

// Iter-27.35 — hook to keep the news unread count in sync across the app
function useNewsUnreadCount() {
    const [count, setCount] = useState(0);
    useEffect(() => {
        let alive = true;
        const fetchCount = () => {
            api.get("/me/news/unread-count")
                .then((r) => alive && setCount(r.data?.unread || 0))
                .catch(() => {});
        };
        fetchCount();
        const onRead = () => fetchCount();
        window.addEventListener("news-read", onRead);
        // Light polling every 60s so admin-created news show up
        const interval = setInterval(fetchCount, 60000);
        return () => {
            alive = false;
            window.removeEventListener("news-read", onRead);
            clearInterval(interval);
        };
    }, []);
    return count;
}

const NAV_ITEMS = [
    { to: "/dashboard", label: "Accueil", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/parcours", label: "Parcours", icon: GraduationCap, testId: "nav-parcours" },
    { to: "/simulator", label: "Simulateur", icon: Sparkles, testId: "nav-simulator" },
    { to: "/terrain", label: "Terrain", icon: Zap, testId: "nav-terrain" },
    { to: "/associations", label: "Associations", icon: Heart, testId: "nav-associations" },
    { to: "/espace-partenaires", label: "Espace Partenaires", icon: HeartHandshake, testId: "nav-partner-spaces" },
    { to: "/leaderboard", label: "Classement", icon: Trophy, testId: "nav-leaderboard" },
    { to: "/profil", label: "Profil", icon: User, testId: "nav-profil" },
];

function Sidebar() {
    const { user } = useAuth();
    const newsUnread = useNewsUnreadCount();

    return (
        <aside
            className="hidden lg:flex flex-col w-60 h-screen px-5 py-5 fixed left-0 top-0 z-30 overflow-y-auto scrollbar-hide"
            data-testid="sidebar"
        >
            <div className="mb-6 flex justify-center shrink-0">
                <Logo
                    variant="full"
                    showAcademy
                    academySize="sm"
                    className="h-[72px] w-auto drop-shadow-[0_0_24px_rgba(238,114,46,0.3)]"
                />
            </div>

            <nav className="space-y-1 flex-1 min-h-0">
                {NAV_ITEMS.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        data-testid={item.testId}
                        className={({ isActive }) =>
                            `relative flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-bold transition-all ${
                                isActive
                                    ? "bg-shariti-orange text-white shadow-lg orange-glow"
                                    : "text-white/60 hover:text-white hover:bg-white/5"
                            }`
                        }
                    >
                        <item.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={2.2} />
                        <span className="flex-1">{item.label}</span>
                        {item.to === "/associations" && newsUnread > 0 && (
                            <span
                                className="relative flex h-2.5 w-2.5 shrink-0"
                                data-testid="sidebar-news-unread-badge"
                                title={`${newsUnread} nouvelle${newsUnread > 1 ? "s" : ""} actualité${newsUnread > 1 ? "s" : ""}`}
                                aria-label={`${newsUnread} nouvelles actualités`}
                            >
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-shariti-orange opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-shariti-orange ring-2 ring-shariti-deep-black"></span>
                            </span>
                        )}
                    </NavLink>
                ))}
                {(user?.role === "admin" || user?.role === "manager") && (
                    <NavLink
                        to="/equipe"
                        data-testid="nav-equipe"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-bold transition-all ${
                                isActive
                                    ? "bg-shariti-orange-light text-shariti-black"
                                    : "text-white/60 hover:text-white hover:bg-white/5"
                            }`
                        }
                    >
                        <UsersIcon className="w-[18px] h-[18px] shrink-0" strokeWidth={2.2} />
                        <span>Équipe</span>
                    </NavLink>
                )}
                {user?.role !== "admin" && user?.role !== "candidate" && (
                    <NavLink
                        to="/cooptation"
                        data-testid="nav-cooptation"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-bold transition-all ${
                                isActive
                                    ? "bg-shariti-orange-light text-shariti-black"
                                    : "text-white/60 hover:text-white hover:bg-white/5"
                            }`
                        }
                    >
                        <UserPlus className="w-[18px] h-[18px] shrink-0" strokeWidth={2.2} />
                        <span>Cooptation</span>
                    </NavLink>
                )}
                {(user?.role === "learner" || user?.role === "manager") &&
                    user?.cash_eligible && (
                        <NavLink
                            to="/paiements"
                            data-testid="nav-payouts"
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-bold transition-all ${
                                    isActive
                                        ? "bg-emerald-500 text-white"
                                        : "text-white/60 hover:text-white hover:bg-white/5"
                                }`
                            }
                        >
                            <Wallet className="w-[18px] h-[18px] shrink-0" strokeWidth={2.2} />
                            <span>Mes paiements</span>
                        </NavLink>
                    )}
                {(user?.role === "admin" || user?.role === "manager") && (
                    <NavLink
                        to="/invitations"
                        data-testid="nav-invitations"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-bold transition-all ${
                                isActive
                                    ? "bg-shariti-orange-light text-shariti-black"
                                    : "text-white/60 hover:text-white hover:bg-white/5"
                            }`
                        }
                    >
                        <UserPlus className="w-[18px] h-[18px] shrink-0" strokeWidth={2.2} />
                        <span>Invitations</span>
                    </NavLink>
                )}
                {user?.role === "admin" && (
                    <NavLink
                        to="/admin"
                        data-testid="nav-admin"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-bold transition-all ${
                                isActive
                                    ? "bg-shariti-yellow text-shariti-black"
                                    : "text-white/60 hover:text-white hover:bg-white/5"
                            }`
                        }
                    >
                        <Settings className="w-[18px] h-[18px] shrink-0" strokeWidth={2.2} />
                        <span>Admin</span>
                    </NavLink>
                )}
            </nav>
        </aside>
    );
}

function BottomTabs() {
    const { user } = useAuth();
    const location = useLocation();
    const scrollRef = useRef(null);
    const activeRef = useRef(null);

    if (!user || user.role === "candidate") return null;

    // The bar is horizontally scrollable on mobile so all role-relevant items fit.
    const items = [...NAV_ITEMS];
    if (user.role === "admin" || user.role === "manager") {
        items.push({
            to: "/equipe",
            label: "Équipe",
            icon: UsersIcon,
            testId: "nav-equipe",
        });
    }
    if (user.role === "learner" || user.role === "manager") {
        items.push({
            to: "/cooptation",
            label: "Cooptation",
            icon: UserPlus,
            testId: "nav-cooptation",
        });
    }
    // Iter-27.13: "Mes paiements" is only visible for users explicitly flagged
    // as cash_eligible by an admin. Avoids confusing the others while we run
    // the early MVP without rémunération.
    if (
        (user.role === "learner" || user.role === "manager") &&
        user.cash_eligible
    ) {
        items.push({
            to: "/paiements",
            label: "Mes paiements",
            icon: Wallet,
            testId: "nav-payouts",
        });
    }
    if (user.role === "admin" || user.role === "manager") {
        items.push({
            to: "/invitations",
            label: "Invitations",
            icon: UserPlus,
            testId: "nav-invitations",
        });
    }
    if (user.role === "admin") {
        items.push({
            to: "/admin",
            label: "Admin",
            icon: Settings,
            testId: "nav-admin",
        });
    }

    return (
        <BottomTabsInner
            items={items}
            scrollRef={scrollRef}
            activeRef={activeRef}
            pathname={location.pathname}
        />
    );
}

// Persisted across route changes (Layout is remounted on every navigation,
// which would otherwise reset scrollLeft to 0 each time).
let _bottomNavScrollLeft = null;

function BottomTabsInner({ items, scrollRef, activeRef, pathname }) {
    // Restore the user's scroll position on every mount. On the very first
    // mount of the session, center the active tab so the bar starts in a
    // sensible state — after that, the user controls the scroll position.
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        if (_bottomNavScrollLeft !== null) {
            container.scrollLeft = _bottomNavScrollLeft;
        } else {
            // First-ever mount: try to center the active tab in the viewport
            // so that neighbors on both sides are visible.
            const target = activeRef.current;
            if (target) {
                const desired =
                    target.offsetLeft -
                    container.clientWidth / 2 +
                    target.offsetWidth / 2;
                container.scrollLeft = Math.max(
                    0,
                    Math.min(
                        desired,
                        container.scrollWidth - container.clientWidth,
                    ),
                );
            }
            _bottomNavScrollLeft = container.scrollLeft;
        }

        const onScroll = () => {
            _bottomNavScrollLeft = container.scrollLeft;
        };
        container.addEventListener("scroll", onScroll, { passive: true });
        return () => container.removeEventListener("scroll", onScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isActive = (to) => {
        if (to === "/dashboard") return pathname === "/dashboard";
        return pathname === to || pathname.startsWith(to + "/");
    };

    return (
        <nav
            className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-3 pb-3 pt-2"
            data-testid="bottom-nav"
        >
            <div
                ref={scrollRef}
                className="glass-card rounded-3xl p-2 overflow-x-auto scrollbar-hide"
                data-testid="bottom-nav-scroll"
            >
                <div className="flex items-center gap-1 w-max">
                    {items.map((item) => {
                        const active = isActive(item.to);
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                ref={active ? activeRef : null}
                                data-testid={`mobile-${item.testId}`}
                                className={`flex flex-col items-center justify-center gap-1 py-2 px-3 rounded-2xl transition-all min-w-[68px] ${
                                    active
                                        ? "bg-shariti-orange text-white"
                                        : "text-white/55 active:bg-white/5"
                                }`}
                            >
                                <item.icon
                                    className="w-5 h-5 shrink-0"
                                    strokeWidth={active ? 2.5 : 2}
                                />
                                <span className="text-[10px] font-bold tracking-tight whitespace-nowrap">
                                    {item.label}
                                </span>
                            </NavLink>
                        );
                    })}
                </div>
            </div>
        </nav>
    );
}

function TopBar({ title, subtitle, action }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const handleLogout = () => {
        setMenuOpen(false);
        logout(); // Iter-33 — logout gère lui-même le redirect (hard)
    };

    // Iter-30.34 — Bloc UserMenu extrait en helper pour pouvoir l'inclure
    // à la fois dans la barre desktop (en haut à droite du titre) et dans
    // l'action row mobile (ligne sous le titre).
    const renderUserMenu = () => (
        <div className="relative" ref={menuRef}>
            <button
                onClick={() => setMenuOpen((v) => !v)}
                data-testid="user-menu-button"
                className="flex items-center gap-2 glass-card rounded-2xl pl-1 pr-2 py-1 hover:border-shariti-orange/40 transition-all"
            >
                {user?.avatar ? (
                    <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-9 h-9 rounded-xl object-cover"
                    />
                ) : (
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-shariti-orange to-shariti-yellow flex items-center justify-center font-bold text-shariti-black text-sm">
                        {user?.name?.[0]?.toUpperCase() || "?"}
                    </div>
                )}
                <ChevronDown className="w-4 h-4 text-white/60" />
            </button>
            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-56 glass-card rounded-2xl overflow-hidden z-50"
                        data-testid="user-menu"
                        style={{ background: "rgba(20,20,20,0.95)" }}
                    >
                        <div className="p-3 border-b border-white/5">
                            <p className="text-sm font-bold text-white truncate">
                                {user?.name}
                            </p>
                            <p className="text-xs text-white/40 truncate">
                                {user?.email}
                            </p>
                            <p className="text-[10px] mt-1 font-bold uppercase tracking-widest text-shariti-orange-light">
                                {user?.role}
                            </p>
                        </div>
                        <button
                            onClick={() => {
                                navigate("/profil");
                                setMenuOpen(false);
                            }}
                            data-testid="user-menu-profile"
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-white/80 hover:bg-white/5 hover:text-white transition-colors"
                        >
                            <User className="w-4 h-4" />
                            Mon profil
                        </button>
                        <button
                            onClick={() => {
                                navigate("/historique");
                                setMenuOpen(false);
                            }}
                            data-testid="user-menu-history"
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-white/80 hover:bg-white/5 hover:text-white transition-colors"
                        >
                            <Flame className="w-4 h-4 text-shariti-orange" />
                            Historique des points
                        </button>
                        <button
                            onClick={handleLogout}
                            data-testid="user-menu-logout"
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-shariti-orange hover:bg-shariti-orange/10 transition-colors"
                        >
                            <LogOut className="w-4 h-4" />
                            Se déconnecter
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    return (
        <header
            className="mb-6 lg:mb-10"
            data-testid="top-bar"
        >
            {/* Mobile-only banner: full-width inspirational subtitle above the title row */}
            {subtitle && (
                <p
                    className="sm:hidden text-[10px] uppercase tracking-[0.22em] text-shariti-orange-light font-thin mb-2 leading-snug"
                    data-testid="page-subtitle-mobile"
                >
                    {subtitle}
                </p>
            )}
            {/* Iter-30.34 — Sur mobile (<640 px) on stack le titre sur la ligne 1
                (full width) puis l'action row sur la ligne 2 (justify-end). Évite
                que le bouton "Podcast" et les contrôles header ne tronquent
                "Pitch Terrain" en "Pitch Terrair" (le `n` était caché). Sur
                desktop on garde l'ancien layout title-left / action-right. */}
            <div className="hidden sm:flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                    {subtitle && (
                        <p className="text-xs uppercase tracking-[0.25em] text-shariti-orange-light font-thin mb-1 truncate">
                            {subtitle}
                        </p>
                    )}
                    <h1
                        className="text-3xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.05] line-clamp-2"
                        data-testid="page-title"
                    >
                        {title}
                    </h1>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    {action}
                    <NotificationsBell />
                    <PointsPill initialPoints={user?.points || 0} />
                    {renderUserMenu()}
                </div>
            </div>
            {/* Mobile layout : titre full-width sur sa propre ligne */}
            <div className="sm:hidden">
                <h1
                    className="text-2xl font-extrabold text-white tracking-tight leading-[1.05] line-clamp-2 mb-3"
                    data-testid="page-title-mobile"
                >
                    {title}
                </h1>
                <div className="flex items-center justify-end gap-1.5 flex-wrap">
                    {action}
                    <NotificationsBell />
                    <PointsPill initialPoints={user?.points || 0} />
                    {renderUserMenu()}
                </div>
            </div>
        </header>
    );
}

export default function Layout({ children, title, subtitle, action }) {
    return (
        <div className="min-h-screen relative bg-shariti-deep-black overflow-hidden">
            {/* Decorative blobs */}
            <div className="pointer-events-none fixed -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-shariti-orange/20 blur-[120px] animate-blob" />
            <div className="pointer-events-none fixed top-1/3 -right-32 w-[400px] h-[400px] rounded-full bg-shariti-yellow/10 blur-[100px] animate-blob" />
            <div className="pointer-events-none fixed bottom-0 left-1/4 w-[400px] h-[400px] rounded-full bg-shariti-orange-light/10 blur-[120px] animate-blob" />

            <Sidebar />

            <motion.main
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="relative z-10 lg:pl-60 pb-28 lg:pb-12 px-4 sm:px-6 lg:px-10 pt-6 lg:pt-10 max-w-[1400px] mx-auto"
            >
                <TopBar title={title} subtitle={subtitle} action={action} />
                {children}
            </motion.main>

            <BottomTabs />
        </div>
    );
}
